<template>
  <div class="admin-apple-transactions-view container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Apple In‑App Käufe</li>
      </ol>
    </nav>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <div>
          <h2 class="mb-0">
            <font-awesome-icon icon="apple-alt" class="me-2" />
            Apple In‑App Käufe
          </h2>
          <p class="text-muted mb-0">Alle Apple In‑App Transaktionen mit Details</p>
        </div>
      </div>
      <BackButton fallback="admin" />
    </div>

    <!-- Controls -->
    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-2 align-items-end">
        <div class="me-auto d-flex flex-wrap gap-2 align-items-end">
          <div class="me-3 text-muted small">Insgesamt: {{ totalCount }} Transaktionen</div>
          <div>
            <label class="form-label me-2 mb-0">Von</label>
            <input type="date" class="form-control d-inline-block" style="width: 170px" v-model="dateFrom" @change="goToPage(1)" />
          </div>
          <div>
            <label class="form-label ms-3 me-2 mb-0">Bis</label>
            <input type="date" class="form-control d-inline-block" style="width: 170px" v-model="dateTo" @change="goToPage(1)" />
          </div>
        </div>
        <div class="input-group" style="width: 220px;">
          <span class="input-group-text">Pro Seite</span>
          <select class="form-select" v-model.number="pageSize" @change="goToPage(1)">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <button class="btn btn-outline-secondary" :disabled="loading" @click="reload">
          <font-awesome-icon icon="sync" class="me-2" />
          Aktualisieren
        </button>
      </div>
    </div>

    

    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Daten werden geladen...</span>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Empty -->
    <div v-else-if="filtered.length === 0" class="text-center py-5">
      <div class="text-muted">
        <font-awesome-icon icon="mobile-alt" class="h1 mb-3" />
        <p>Keine Apple In‑App Transaktionen gefunden.</p>
      </div>
    </div>

    <!-- Table -->
    <div v-else class="card">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 14%">Nummer</th>
              <th style="width: 18%">Kunde</th>
              <th style="width: 10%">Produkt</th>
              <th style="width: 12%">Preis</th>
              <th style="width: 14%">Transaktions-ID</th>
              <th style="width: 12%">Kaufdatum</th>
              <th style="width: 8%">Status</th>
              <th style="width: 12%">Umgebung</th>
              <th style="width: 12%">Apple API</th>
              <th style="width: 14%">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="tx in paged" :key="tx.id">
              <tr class="align-middle">
              <td>
                <div class="fw-bold text-primary">{{ tx.invoiceNumber || '–' }}</div>
                <div class="text-muted small">Rechnung</div>
              </td>
              <td>
                <div class="fw-semibold">{{ tx.customerName }}</div>
                <div class="text-muted small">{{ tx.customerEmail }}</div>
              </td>
              <td>
                <div class="fw-semibold">{{ tx.paymentDetails?.productId || '–' }}</div>
                <div class="text-muted small">{{ tx.coins ? tx.coins + ' Coins' : '' }}</div>
              </td>
              <td>
                <div class="fw-bold text-success">{{ formatCurrency(tx.price) }}</div>
                <div class="text-muted small">{{ tx.paymentDetails?.currency || 'EUR' }}</div>
              </td>
              <td>
                <div class="text-truncate" style="max-width:200px" :title="tx.paymentDetails?.transactionId || ''">
                  {{ tx.paymentDetails?.transactionId || '–' }}
                </div>
              </td>
              <td>
                <div>{{ formatDate(tx.paymentDate || tx.createdAt) }}</div>
                <div class="text-muted small">{{ tx.paymentDetails?.storefront || '' }}</div>
              </td>
              <td>
                <span :class="getStatusBadgeClass(tx.status)" class="badge fs-6">{{ getStatusDisplayName(tx.status) }}</span>
              </td>
              <td>
                <span class="badge bg-secondary">{{ tx.paymentDetails?.environment || 'Production' }}</span>
              </td>
              <td>
                <button class="btn btn-sm btn-outline-primary" :disabled="tx._loadingApple" @click="fetchAppleHistory(tx)">
                  <span v-if="tx._loadingApple" class="spinner-border spinner-border-sm me-1" />
                  History
                </button>
              </td>
              <td>
                <button class="btn btn-sm btn-warning" :disabled="tx._checkingManually" @click="manualCheck(tx)">
                  <span v-if="tx._checkingManually" class="spinner-border spinner-border-sm me-1" />
                  Manuell prüfen
                </button>
              </td>
              </tr>
              <tr v-if="tx._showHistory" :key="tx.id + '-details'">
                <td :colspan="9">
                  <div class="p-3 bg-light border rounded">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="fw-semibold">Apple API Antwort</div>
                      <button class="btn btn-sm btn-outline-secondary" @click="tx._showHistory = false">Schließen</button>
                    </div>
                    <div class="small text-muted mb-2">
                      Transaction ID: <span class="fw-semibold">{{ tx._appleHistory?.transactionId || tx.paymentDetails?.transactionId }}</span>
                      • Items: <span class="fw-semibold">{{ tx._appleHistory?.transactions?.length || 0 }}</span>
                    </div>
                    <pre class="mb-0" style="white-space: pre-wrap; word-break: break-word; max-height: 420px; overflow: auto;">{{ prettyJson(tx._appleHistory?.transactions) }}</pre>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
      <nav aria-label="Apple Transactions Pagination">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: page <= 1 }">
            <button class="page-link" @click="goToPage(page - 1)" :disabled="page <= 1">Zurück</button>
          </li>
          <li v-for="p in pageNumbers" :key="p" class="page-item" :class="{ active: p === page }">
            <button class="page-link" @click="goToPage(p)">{{ p }}</button>
          </li>
          <li class="page-item" :class="{ disabled: page >= totalPages }">
            <button class="page-link" @click="goToPage(page + 1)" :disabled="page >= totalPages">Weiter</button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
  
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { adminApi } from '@/api/admin'

const loading = ref(false)
const error = ref(null)
const allTx = ref([])
const inAppBillings = ref([])

// Standard: letzte 7 Tage
const today = new Date()
const sevenDaysAgo = new Date()
sevenDaysAgo.setDate(today.getDate() - 6)
const dateFrom = ref(sevenDaysAgo.toISOString().slice(0, 10))
const dateTo = ref(today.toISOString().slice(0, 10))

const page = ref(1)
const pageSize = ref(20)

const reload = async () => {
  await load()
}

const load = async () => {
  if (loading.value) return
  loading.value = true
  error.value = null
  try {
    // Timeout-Schutz: bricht UI-Warten nach 20s ab
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Zeitüberschreitung bei der Anfrage')), 20000))

    // Nur noch: In‑App-Billings (Apple)
    const bills = await Promise.race([
      adminApi.getAppleInAppBillings({
      dateFrom: dateFrom.value,
      dateTo: dateTo.value,
      page: page.value,
      limit: pageSize.value,
      }),
      timeout,
    ])
    inAppBillings.value = (bills.billings || []).map(b => ({
      id: b.id,
      invoiceNumber: b.invoiceNumber,
      customerName: `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email,
      customerEmail: b.email,
      paymentDetails: b.paymentDetails,
      price: b.price,
      coins: b.coins,
      status: b.status,
      paymentDate: b.paymentDate,
      createdAt: b.createdAt,
      _loadingApple: false,
      _showHistory: false,
      _appleHistory: null,
    }))
    // Pagination aus Billings übernehmen
    page.value = bills.pagination?.page || page.value
    pageSize.value = bills.pagination?.limit || pageSize.value
    serverTotal.value = bills.pagination?.total || inAppBillings.value.length
    serverTotalPages.value = bills.pagination?.totalPages || 1
  } catch (err) {
    console.error('Error loading apple transactions:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Apple Transaktionen'
  } finally {
    loading.value = false
  }
}

const serverTotal = ref(0)
const serverTotalPages = ref(1)
// Anzeigen: kombinierte Liste aus SalesReport-Transaktionen und lokalen In-App-Billings
const filtered = computed(() => inAppBillings.value)

const totalCount = computed(() => serverTotal.value || filtered.value.length)
const totalPages = computed(() => serverTotalPages.value || Math.max(1, Math.ceil(totalCount.value / pageSize.value)))
const pageNumbers = computed(() => {
  const current = page.value
  const total = totalPages.value
  const range = []
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  if (end - start < 4) {
    if (start === 1) end = Math.min(total, start + 4)
    else start = Math.max(1, end - 4)
  }
  for (let i = start; i <= end; i++) range.push(i)
  return range
})

const paged = computed(() => filtered.value)

const goToPage = async (p) => {
  if (p < 1 || p > totalPages.value) return
  page.value = p
  await load()
}

const formatCurrency = (amount) => {
  if (!amount) return '€0,00'
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleString('de-DE')
}

const getStatusDisplayName = (status) => {
  switch (status) {
    case 'pending':
      return 'Ausstehend'
    case 'completed':
      return 'Abgeschlossen'
    case 'failed':
      return 'Fehlgeschlagen'
    case 'cancelled':
      return 'Storniert'
    default:
      return status
  }
}

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pending':
      return 'badge-pending'
    case 'completed':
      return 'badge-completed'
    case 'failed':
      return 'badge-failed'
    case 'cancelled':
      return 'badge-cancelled'
    default:
      return 'badge-secondary'
  }
}

// Receipt Lookup entfernt

const fetchAppleHistory = async (row) => {
  if (!row?.paymentDetails?.transactionId) return
  row._loadingApple = true
  try {
    const res = await adminApi.getAppleTransactionHistoryById(row.paymentDetails.transactionId)
    row._appleHistory = res?.data || null
    row._showHistory = true
  } catch (err) {
    console.error('Apple history error', err)
    row._appleHistory = { error: err?.response?.data?.message || 'Fehler beim Abrufen der Apple-Historie' }
    row._showHistory = true
  } finally {
    row._loadingApple = false
  }
}

const manualCheck = async (row) => {
  if (!row?.id) return
  row._checkingManually = true
  try {
    await adminApi.checkAppleInAppBilling(row.id)
    await load()
  } catch (err) {
    console.error('Manual IAP check failed', err)
    alert(err?.response?.data?.message || 'Fehler bei der manuellen Prüfung')
  } finally {
    row._checkingManually = false
  }
}

const prettyJson = (obj) => {
  try {
    return JSON.stringify(obj || {}, null, 2)
  } catch {
    return String(obj)
  }
}

onMounted(() => {
  load()
})

// Reaktive Filter-Reloads
watch(dateFrom, async () => {
  page.value = 1
  await load()
})
watch(dateTo, async () => {
  page.value = 1
  await load()
})
</script>

<style scoped>
</style>


